import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  BoxProps,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { UPDATE_ORGANISATION_BILLING_INFORMATION } from '../../graphql/mutations';
import { AuthDispatchContext, AuthStateContext } from '../../contexts/AuthContext';
import useToggle from '../../hooks/useToggle';

const PricingWrapper = (props: BoxProps & { highlight?: boolean }) => {
  const { highlight, ...rest } = props;

  const popularStyles: BoxProps = {
    borderWidth: '2px',
    borderColor: 'blue.300'
  };

  const styles = highlight ? popularStyles : null;

  return (
    <Box
      w='full'
      mx='auto'
      bg={useColorModeValue('brand.100', 'brand.800')}
      px='10'
      pt='8'
      pb='8'
      rounded='lg'
      shadow='base'
      position='relative'
      {...styles}
      {...rest}
    />
  );
};

const UpdateBillingInformation = () => {
  const { currentOrganisation } = useContext(AuthStateContext);
  const { refreshUser } = useContext(AuthDispatchContext);
  const [editMode, toggleEditMode] = useToggle(false);
  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState(null);
  const [updateOrganisationBillingInformation] = useMutation(UPDATE_ORGANISATION_BILLING_INFORMATION);

  const registerSchema = yup.object({
    name: yup.string().required('Name is required'),
    taxNumber: yup.string().optional(),
    email: yup.string().required('Email is required').email('Email address is not valid'),
    line1: yup.string().required('Address is required'),
    line2: yup.string().optional(),
    country: yup.string().required('Country is required'),
    state: yup.string().required('State is required'),
    code: yup.string().required('Code is required')
  });
  // load url using next router
  useEffect(() => {
    const location = router;
    // eslint-disable-next-line no-console
    console.log('router', location);
  }, [router.asPath]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateOrganisation = async (evt) => {
    await updateOrganisationBillingInformation({
      variables: {
        input: {
          orgId: currentOrganisation.id,
          billingInfo: {
            email: evt.email,
            name: evt.name,
            taxNumber: evt.taxNumber,
            address: {
              line1: evt.line1,
              line2: evt.line2,
              country: evt.country,
              code: evt.code,
              state: evt.state
            }
          }
        }
      }
    })
      .then(async () => {
        await refreshUser();
      })
      .catch((err) => {
        setError(err);
      });

    toggleEditMode();
  };

  return (
    <PricingWrapper minHeight='80'>
      {error && (
        <Alert status='error'>
          <AlertIcon />
        </Alert>
      )}
      <Formik
        validationSchema={registerSchema}
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={handleUpdateOrganisation}
        initialValues={{
          name: currentOrganisation.billing?.name || '',
          email: currentOrganisation.billing?.email || '',
          taxNumber: currentOrganisation.billing?.taxNumber || '',
          line1: currentOrganisation.billing?.address?.line1 || '',
          line2: currentOrganisation.billing?.address?.line2 || '',
          country: currentOrganisation.billing?.address?.country || '',
          code: currentOrganisation.billing?.address?.code || '',
          state: currentOrganisation.billing?.address?.state || ''
        }}
        enableReinitialize
      >
        {({ isSubmitting, handleChange, errors, handleBlur }) => (
          <Form>
            <Flex direction='column' justify='center'>
              <Text fontSize='2xl' fontWeight='bold' pb='6'>
                Billing
              </Text>

              {!currentOrganisation.billing && !editMode && <Text size='md'>Billing has not been set up.</Text>}
              {currentOrganisation.billing && !editMode && (
                <>
                  <Text size='md'>Name</Text>
                  <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')} pb='4'>
                    {currentOrganisation.billing?.name}
                  </Text>
                  <Text size='md'>Email</Text>
                  <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')} pb='4'>
                    {currentOrganisation.billing?.email}
                  </Text>
                  <Text size='md'>VAT/GST Number</Text>
                  <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')} pb='4'>
                    {currentOrganisation.billing?.taxNumber}
                  </Text>

                  <Text size='md'>Address</Text>
                  <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')} pb='4'>
                    {`${currentOrganisation.billing?.address?.line1}, 
                    ${currentOrganisation.billing?.address?.country}, 
                    ${currentOrganisation.billing?.address?.code}`}
                  </Text>
                </>
              )}
              {editMode && (
                <Stack>
                  <Field name='name'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.name && !!form.errors.name}
                        mb={2}
                      >
                        <FormLabel size='md'>Name</FormLabel>
                        <Input {...field} placeholder='Name' id='name' name='name' type='text' required autoFocus />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.name}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name='email'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.email && !!form.errors.email}
                        mb={2}
                      >
                        <FormLabel size='md'>Email</FormLabel>
                        <Input {...field} placeholder='Email' id='email' name='email' type='text' required />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name='taxNumber'>
                    {({ field, form }) => (
                      <FormControl
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.taxNumber && !!form.errors.taxNumber}
                        mb={2}
                      >
                        <FormLabel size='md'>VAT/GST Number</FormLabel>
                        <Input {...field} placeholder='VAT/GST Number' id='taxNumber' name='taxNumber' type='text' />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.taxNumber}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name='line1'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.line1 && !!form.errors.line1}
                        mb={2}
                      >
                        <FormLabel size='md'>Address line1</FormLabel>
                        <Input {...field} placeholder='Address line1' id='line1' name='line1' type='text' required />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.line1}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name='line2'>
                    {({ field, form }) => (
                      <FormControl
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.line2 && !!form.errors.line2}
                        mb={2}
                      >
                        <FormLabel size='md'>Address line2</FormLabel>
                        <Input {...field} placeholder='Address line2' id='line2' name='line2' type='text' />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.line2}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name='country'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.country && !!form.errors.country}
                        mb={2}
                      >
                        <FormLabel size='md'>Country</FormLabel>
                        <Input {...field} placeholder='Country' id='country' name='country' type='text' required />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.country}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name='code'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.code && !!form.errors.code}
                        mb={2}
                      >
                        <FormLabel size='md'>Code</FormLabel>
                        <Input {...field} placeholder='Code' id='code' name='code' type='text' required />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.code}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name='state'>
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={form.touched.state && !!form.errors.state}
                        mb={2}
                      >
                        <FormLabel size='md'>State</FormLabel>
                        <Input {...field} placeholder='State' id='state' name='state' type='text' required />
                        {/* @ts-ignore TODO: Fix */}
                        <FormErrorMessage mb={2}>{errors.state}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
              )}
            </Flex>

            {editMode ? (
              <Flex flexDir='row' justifyContent='space-between' w='100%' mt={8}>
                <Button
                  w='min-intrinsic'
                  minH='3.5rem'
                  rounded='lg'
                  fontWeight='bold'
                  colorScheme='brand'
                  variant='outline'
                  onClick={() => toggleEditMode(true)}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  w='min-intrinsic'
                  minH='3.5rem'
                  rounded='lg'
                  fontWeight='bold'
                  colorScheme='brand'
                  variant='outline'
                  isLoading={isSubmitting}
                  isDisabled={!!errors.name && !!errors.line1 && !!errors.line2}
                >
                  Save
                </Button>
              </Flex>
            ) : (
              // <div
              //   style={{
              //     position: 'absolute',
              //     display: 'flex',
              //     alignItems: 'center',
              //     justifyContent: 'center',
              //     bottom: 0,
              //     left: 0,
              //     width: '100%',
              //     marginBottom: '20px',
              //     marginTop: '20px'
              //   }}
              // >
              <Flex flexDir='row' justifyContent='center' w='100%' mt={8}>
                <Button
                  w='min-intrinsic'
                  minH='3.5rem'
                  rounded='lg'
                  fontWeight='bold'
                  colorScheme='brand'
                  variant='outline'
                  onClick={() => toggleEditMode(true)}
                >
                  Manage Billing
                </Button>
              </Flex>
              // </div>
            )}
          </Form>
        )}
      </Formik>
    </PricingWrapper>
  );
};

export default UpdateBillingInformation;
